import { css } from '@emotion/core';
import { IconButton, List, ListItem, ListItemText, Snackbar, Typography, useTheme } from '@material-ui/core';
import { Theme } from '@material-ui/core/styles';
import CloseIcon from '@material-ui/icons/Close';
import { Skeleton } from '@material-ui/lab';
import * as React from 'react';
import { SearchResultsProvider } from '../../hooks/use-agent-search-draft';

import { AgentComposer, AgentComposerHandle } from '../chat/agent-composer';
import { ChatMarkdown } from '../chat/chat-markdown';
import { AgentFeedItemErrorBoundary } from './agent-feed-item-error-boundary';
import { pageContentOverrides, styles } from './agent-page.styles';
import { ChatEvent } from './agent-types';
import { ChartFeedPreview } from './chart-feed-preview';
import { EmailDraftFeed } from './email-draft-feed';
import { SearchPreviewFeed } from './search-preview-feed';
import { SubmissionDraftFeed } from './submission-draft-feed';
import { TableFeedPreview } from './table-feed-preview';
import { UpdateDraftFeed } from './update-draft-feed';
import { ConversationState } from './use-conversation-state';
import { formatDuration } from './utils';

const SNACKBAR_DURATION_MS = 4000;
const SKELETON_HEIGHT_USER = 40;
const SKELETON_HEIGHT_ASSISTANT_PRIMARY = 60;
const SKELETON_HEIGHT_ASSISTANT_SECONDARY = 50;

// Helper function to render error/abort events
function renderEventRow(event: ChatEvent, key: string): JSX.Element {
    const isAssistantSide = true;
    const eventRowClass = `agent__row ${isAssistantSide ? 'agent__row--assistant' : ''}`;
    let content = event.kind === 'compact_boundary' ? event.label : `${event.kind}: ${event.label}`;
    if (typeof event.durationMs === 'number') content += ` (${formatDuration(event.durationMs)})`;
    if (event.details) content += ` — ${event.details}`;
    const isError = event.kind === 'error' || event.kind === 'aborted';
    const rowClass = `agent__eventRow${isError ? ' is-error' : ''}`;
    const contentNode = <div className={`agent__assistantBlock ${rowClass}`}>{content}</div>;
    return (
        <ListItem key={key} alignItems="flex-start" className={eventRowClass} aria-label="Event">
            <ListItemText secondary={contentNode} secondaryTypographyProps={{ color: 'initial' }} />
        </ListItem>
    );
}

interface AgentContentProps {
    state: ConversationState;
    variant?: 'page' | 'sidebar';
}

export const AgentContent: React.FC<AgentContentProps> = ({ state, variant = 'page' }) => {
    const theme = useTheme<Theme>();
    const {
        input,
        setInput,
        streaming,
        recovering,
        assistantBuffer,
        feed,
        error,
        offline,
        snackOpen,
        showIntro,
        open,
        historyLoading,
        model,
        modelLoading,
        effort,
        handleSend,
        handleStop,
        handleSnackClose,
        setModel,
        setEffort
    } = state;

    const endRef = React.useRef<HTMLDivElement | null>(null);
    const composerRef = React.useRef<AgentComposerHandle>(null);
    const layoutClass = variant === 'sidebar' ? 'agent__sidebar' : '';
    const rootClassName = [showIntro ? 'agent agent--intro' : 'agent agent--chat', layoutClass]
        .filter(Boolean)
        .join(' ');

    const rootCss = React.useMemo(() => {
        const base = styles(theme);
        return variant === 'page' ? css([base, pageContentOverrides]) : base;
    }, [theme, variant]);

    // Auto-scroll to bottom when feed changes
    React.useEffect(() => {
        if (endRef.current) {
            try {
                const scrollContainer = endRef.current.closest('.agent__body') as HTMLElement | null;
                if (scrollContainer) {
                    scrollContainer.scrollTop = scrollContainer.scrollHeight;
                }
            } catch {
                /* noop */
            }
        }
    }, [feed, assistantBuffer]);

    const handleContentClick = (event: React.MouseEvent<HTMLElement>) => {
        const target = event.target as HTMLElement;

        // Don't focus if clicking within interactive elements or composer
        if (target.closest('button, a, textarea, input, .agent__composer, .agent__headerActions')) {
            return;
        }

        // Don't focus if user is selecting text
        const selection = window.getSelection();
        if (selection && selection.toString().length > 0) {
            return;
        }

        composerRef.current?.focus();
    };

    // Build feed rows
    const feedRows: JSX.Element[] = [];

    feed.forEach((item, index) => {
        const baseKey = `${item.ts}-${item.kind}`;

        // Render user messages
        if (item.kind === 'user_text') {
            const messageKey = `msg-${baseKey}-${index}`;
            const bubbleClass = 'agent__text agent__text--user';
            const secondaryNode = (
                <div className={bubbleClass}>
                    <ChatMarkdown>{item.content}</ChatMarkdown>
                </div>
            );
            const rowClassName = 'agent__row agent__row--user';
            const listTextClass = 'agent__listText agent__listText--user';
            const secondaryTypographyProps = {
                className: 'agent__secondary agent__secondary--user',
                color: 'initial' as 'initial',
                component: 'div' as 'div'
            };
            feedRows.push(
                <ListItem key={messageKey} alignItems="flex-start" className={rowClassName} aria-label="User message">
                    <ListItemText
                        secondary={secondaryNode}
                        className={listTextClass}
                        secondaryTypographyProps={secondaryTypographyProps}
                    />
                </ListItem>
            );
            return;
        }

        // Render assistant messages
        if (item.kind === 'assistant_text') {
            const messageKey = `msg-${baseKey}-${index}`;
            const bubbleClass = 'agent__text';
            const secondaryNode = <ChatMarkdown className={bubbleClass}>{item.content}</ChatMarkdown>;
            const rowClassName = 'agent__row agent__row--assistant';
            const listTextClass = 'agent__listText agent__listText--assistant';
            const secondaryTypographyProps = {
                className: 'agent__secondary',
                color: 'initial' as 'initial',
                component: 'div' as 'div'
            };
            feedRows.push(
                <ListItem
                    key={messageKey}
                    alignItems="flex-start"
                    className={rowClassName}
                    aria-label="Assistant message"
                >
                    <ListItemText
                        secondary={secondaryNode}
                        className={listTextClass}
                        secondaryTypographyProps={secondaryTypographyProps}
                    />
                </ListItem>
            );
            return;
        }

        // Render tables
        if (item.kind === 'table') {
            feedRows.push(
                <ListItem
                    key={`table-${item.ts}-${index}`}
                    alignItems="flex-start"
                    className="agent__row agent__row--assistant"
                    aria-label="Data table"
                >
                    <ListItemText
                        secondary={<TableFeedPreview table={item.table} />}
                        secondaryTypographyProps={{ color: 'initial', component: 'div' as 'div' }}
                    />
                </ListItem>
            );
            return;
        }

        // Render charts
        if (item.kind === 'chart') {
            feedRows.push(
                <ListItem
                    key={`chart-${item.ts}-${index}`}
                    alignItems="flex-start"
                    className="agent__row agent__row--assistant"
                    aria-label="Data chart"
                >
                    <ListItemText
                        secondary={<ChartFeedPreview chart={item.chart} />}
                        secondaryTypographyProps={{ color: 'initial', component: 'div' as 'div' }}
                    />
                </ListItem>
            );
            return;
        }

        if (item.kind === 'email_draft') {
            feedRows.push(
                <ListItem
                    key={`email-draft-${item.ts}-${index}`}
                    alignItems="flex-start"
                    className="agent__row agent__row--assistant"
                    aria-label="Email draft"
                >
                    <ListItemText
                        secondary={<EmailDraftFeed draftEmailId={item.draftEmailId} />}
                        secondaryTypographyProps={{ color: 'initial', component: 'div' as 'div' }}
                    />
                </ListItem>
            );
            return;
        }

        if (item.kind === 'submission_draft') {
            feedRows.push(
                <ListItem
                    key={`submission-draft-${item.ts}-${index}`}
                    alignItems="flex-start"
                    className="agent__row agent__row--assistant"
                    aria-label="Submission draft"
                >
                    <ListItemText
                        secondary={<SubmissionDraftFeed draftSubmissionId={item.draftSubmissionId} />}
                        secondaryTypographyProps={{ color: 'initial', component: 'div' as 'div' }}
                    />
                </ListItem>
            );
            return;
        }

        if (item.kind === 'client_update_draft') {
            feedRows.push(
                <ListItem
                    key={`client-update-draft-${item.ts}-${index}`}
                    alignItems="flex-start"
                    className="agent__row agent__row--assistant"
                    aria-label="Client update draft"
                >
                    <ListItemText
                        secondary={<UpdateDraftFeed draftId={item.draftClientUpdateId} draftType="client" />}
                        secondaryTypographyProps={{ color: 'initial', component: 'div' as 'div' }}
                    />
                </ListItem>
            );
            return;
        }

        if (item.kind === 'job_update_draft') {
            feedRows.push(
                <ListItem
                    key={`job-update-draft-${item.ts}-${index}`}
                    alignItems="flex-start"
                    className="agent__row agent__row--assistant"
                    aria-label="Job update draft"
                >
                    <ListItemText
                        secondary={<UpdateDraftFeed draftId={item.draftJobUpdateId} draftType="job" />}
                        secondaryTypographyProps={{ color: 'initial', component: 'div' as 'div' }}
                    />
                </ListItem>
            );
            return;
        }

        if (item.kind === 'search_preview') {
            const resultsFeed = (
                <SearchResultsProvider searchDraftId={item.searchDraftId}>
                    <SearchPreviewFeed />
                </SearchResultsProvider>
            );
            feedRows.push(
                <ListItem
                    key={`search-preview-${item.ts}-${index}`}
                    alignItems="flex-start"
                    className="agent__row agent__row--assistant"
                    aria-label="Search preview"
                >
                    <ListItemText
                        secondary={resultsFeed}
                        secondaryTypographyProps={{ color: 'initial', component: 'div' as 'div' }}
                    />
                </ListItem>
            );
            return;
        }

        // Show durable compaction markers alongside error/abort notices.
        if (item.kind === 'event') {
            if (
                item.event.kind === 'error' ||
                item.event.kind === 'aborted' ||
                item.event.kind === 'compact_boundary'
            ) {
                const eventKey = `${item.event.id ?? baseKey}-${index}`;
                feedRows.push(renderEventRow(item.event, eventKey));
            }
        }
    });

    // Handle streaming assistant buffer
    if (assistantBuffer?.trim()) {
        const bubbleClass = 'agent__text';
        const secondaryNode = <ChatMarkdown className={bubbleClass}>{assistantBuffer}</ChatMarkdown>;
        const rowClassName = 'agent__row agent__row--assistant';
        const listTextClass = 'agent__listText agent__listText--assistant';
        const secondaryTypographyProps = {
            className: 'agent__secondary',
            color: 'initial' as 'initial',
            component: 'div' as 'div'
        };
        feedRows.push(
            <ListItem
                key="streaming-buffer"
                alignItems="flex-start"
                className={rowClassName}
                aria-label="Assistant message (streaming)"
            >
                <ListItemText
                    secondary={secondaryNode}
                    className={listTextClass}
                    secondaryTypographyProps={secondaryTypographyProps}
                />
            </ListItem>
        );
    }

    // Show simple "Thinking..." indicator when streaming
    if (streaming) {
        const thinkingNode = (
            <div className="agent__assistantBlock agent__assistantBlock--wide">
                <div className="agent__thinkingSection">
                    <div className="agent__thinkingSummary">
                        <span className="agent__thinkingLabel">
                            Thinking
                            <span className="agent__typing">
                                <span className="agent__dot" />
                                <span className="agent__dot" />
                                <span className="agent__dot" />
                            </span>
                        </span>
                    </div>
                </div>
            </div>
        );
        feedRows.push(
            <ListItem
                key="streaming-indicator"
                alignItems="flex-start"
                className="agent__row agent__row--assistant"
                aria-label="Thinking"
            >
                <ListItemText
                    secondary={thinkingNode}
                    secondaryTypographyProps={{ color: 'initial', component: 'div' as 'div' }}
                />
            </ListItem>
        );
    }

    if (recovering) {
        const recoveringNode = (
            <div className="agent__assistantBlock agent__assistantBlock--wide">
                <div className="agent__thinkingSection">
                    <div className="agent__thinkingSummary">
                        <span className="agent__thinkingLabel">
                            Reconnecting — Orbit is still working
                            <span className="agent__typing">
                                <span className="agent__dot" />
                                <span className="agent__dot" />
                                <span className="agent__dot" />
                            </span>
                        </span>
                    </div>
                </div>
            </div>
        );
        feedRows.push(
            <ListItem
                key="recovering-indicator"
                alignItems="flex-start"
                className="agent__row agent__row--assistant"
                aria-label="Reconnecting"
            >
                <ListItemText
                    secondary={recoveringNode}
                    secondaryTypographyProps={{ color: 'initial', component: 'div' as 'div' }}
                />
            </ListItem>
        );
    }

    const guardedFeedRows = feedRows.map((feedRow, index) => {
        const itemKey = feedRow.key?.toString() || `feed-row-${index}`;
        return (
            <AgentFeedItemErrorBoundary key={itemKey} itemKey={itemKey}>
                {feedRow}
            </AgentFeedItemErrorBoundary>
        );
    });

    const offlineBanner = offline ? (
        <div className="agent__offlineBanner">
            <Typography variant="caption">Offline – check your connection.</Typography>
        </div>
    ) : null;

    const snackbarAnchorOrigin = { vertical: 'bottom' as 'bottom', horizontal: 'center' as 'center' };
    const snackAction = (
        <IconButton size="small" aria-label="close" color="inherit" onClick={handleSnackClose}>
            <CloseIcon fontSize="small" />
        </IconButton>
    );

    const introContent = (
        <>
            {offlineBanner}
            <div className="agent__intro">
                <div className="agent__intro--header">
                    <img src="/images/orbit-transparent-small.png" alt="Orbit logo" className="agent__introLogo" />
                    <Typography variant="h4" className="agent__introHeading">
                        How can I help you today?
                    </Typography>
                </div>
                <AgentComposer
                    ref={composerRef}
                    className="agent__composer--intro"
                    autoFocus={open && !offline}
                    value={input}
                    onChange={setInput}
                    onSend={handleSend}
                    onStop={handleStop}
                    isStreaming={streaming || recovering}
                    isOffline={offline}
                    placeholder="Type a message…"
                    model={model}
                    modelLoading={modelLoading}
                    effort={effort}
                    onModelChange={setModel}
                    onEffortChange={setEffort}
                />
            </div>
        </>
    );

    const skeletonRows = Array.from({ length: 3 }).map((_, index) => (
        <div key={index} className="agent__skeletonRow">
            <Skeleton variant="text" width="60%" height={SKELETON_HEIGHT_USER} style={{ marginLeft: 'auto' }} />
            <Skeleton variant="text" width="80%" height={SKELETON_HEIGHT_ASSISTANT_PRIMARY} />
            <Skeleton variant="text" width="75%" height={SKELETON_HEIGHT_ASSISTANT_SECONDARY} />
        </div>
    ));

    const loadingSkeletons =
        historyLoading && feed.length === 0 ? <div className="agent__loadingSkeletons">{skeletonRows}</div> : null;

    const transcriptContent = (
        <>
            {offlineBanner}
            <div className="agent__transcript">
                <List className="agent__list" aria-live="polite">
                    {loadingSkeletons || guardedFeedRows}
                    <div ref={endRef} />
                </List>
            </div>
        </>
    );

    const mainContent = showIntro ? introContent : transcriptContent;

    const composerFooter = showIntro ? null : (
        <footer className="agent__composerDock">
            <AgentComposer
                ref={composerRef}
                className="agent__composer--dock"
                autoFocus={open && !offline}
                value={input}
                onChange={setInput}
                onSend={handleSend}
                onStop={handleStop}
                isStreaming={streaming || recovering}
                isOffline={offline}
                placeholder="Type a message…"
                model={model}
                modelLoading={modelLoading}
                effort={effort}
                onModelChange={setModel}
                onEffortChange={setEffort}
            />
        </footer>
    );

    return (
        <div css={rootCss} className={rootClassName}>
            <main className="agent__body" onClick={handleContentClick}>
                <div className={showIntro ? 'agent__content agent__content--intro' : 'agent__content'}>
                    {mainContent}
                </div>
            </main>
            {composerFooter}
            <Snackbar
                anchorOrigin={snackbarAnchorOrigin}
                open={snackOpen}
                autoHideDuration={SNACKBAR_DURATION_MS}
                onClose={handleSnackClose}
                message={error}
                action={snackAction}
            />
        </div>
    );
};
